import {
  faDownload,
  faShield,
  faShieldCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { Anchor, Button, Card, Flex, Text } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import { type CoreInsurance } from '@flecto/types'
import { Icon, TextCopy, TextDate, useMediaQuery } from '@flecto/ui/@shared'
import { formatDateString, useTimeUntil } from '@flecto/utils'

import { useBookingInsuranceStatus } from '../../../booking-insurances/hooks/useBookingInsuranceStatus'

export function PolicyDetails({ insurance }: { insurance: CoreInsurance }) {
  if (!insurance) return null

  const { isMobile } = useMediaQuery()

  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })

  const {
    meta_status,
    policy_number,
    booking,
    end_at,
    terms_url,
    created_at,
    start_at,
  } = insurance

  const startIn = useTimeUntil(start_at)

  const { color } = useBookingInsuranceStatus({ insurance })

  const insuranceActiveOrWithinClaimPeriod =
    meta_status === 'active' || meta_status === 'within_claim_period'

  const statusIcon = meta_status === 'active' ? faShieldCheck : faShield

  return (
    <Card>
      <Flex
        justify='space-between'
        wrap={isMobile ? 'wrap' : 'nowrap'}
        align='center'
      >
        <Flex gap='md' align={isMobile ? 'flex-start' : 'center'}>
          <Icon
            icon={statusIcon}
            withCircleBackground
            color={color}
            size='sm'
            style={{ flexShrink: 0 }}
          />

          <Flex direction='column'>
            <Flex gap={isMobile ? '' : 'xs'} wrap='wrap'>
              <Text fw={500}>{t('insurances_details.policy_number')}</Text>
              {isMobile ? (
                <>
                  <Text fw={500}>{policy_number}</Text>
                  <TextCopy
                    fw={500}
                    iconColor='gray.7'
                    withLabel={false}
                    pos='absolute'
                    right={20}
                  >
                    {policy_number}
                  </TextCopy>
                </>
              ) : (
                <TextCopy fw={500} iconColor='gray.7'>
                  {policy_number}
                </TextCopy>
              )}
            </Flex>

            <Flex gap={isMobile ? 'xs' : 'sm'} c='gray.7' wrap='wrap'>
              <Text>
                {booking
                  ? t('insurances_details.issued_for')
                  : t('insurances_details.issued_on')}
                {booking && (
                  <Anchor
                    fw={500}
                    c='blue'
                    href={`/bookings/${booking.internal_id}`}
                  >
                    {t('insurances_details.booking', {
                      replace: { value: booking.internal_id },
                    })}
                  </Anchor>
                )}
                {booking && t('insurances_details.on_date')}
                {formatDateString({
                  date: created_at,
                  variant: 'datetime-brief',
                })}
              </Text>

              {insurance.meta_status === 'scheduled' && (
                <>
                  {!isMobile && <>•</>}
                  <Text>
                    {t('insurances_details.scheduled_to_activate_in', {
                      date: startIn,
                    })}
                  </Text>
                </>
              )}

              {insuranceActiveOrWithinClaimPeriod && (
                <>
                  {!isMobile && <>•</>}
                  <Text>
                    {t('insurances_details.claim_period_ends_on')}

                    <TextDate
                      fw={500}
                      display='inline'
                      date={end_at}
                      variant='datetime-brief'
                    />
                  </Text>
                </>
              )}
            </Flex>
          </Flex>
        </Flex>

        <Button
          size='sm'
          variant='default'
          leftSection={<Icon icon={faDownload} size='sm' />}
          component='a'
          target='_blank'
          c='gray.7'
          href={terms_url}
          style={{ flexShrink: 0 }}
          mt={isMobile ? 'sm' : 0}
          ml={isMobile ? 45 : 0}
        >
          {t('insurances_details.insurance_letter_button')}
        </Button>
      </Flex>
    </Card>
  )
}
